import { shouldSendMetrics } from "config/envVariables";

import { NiraAuth0UserType } from "types/auth0UserType";
import { TeamId } from "types/userType";

// Amplitude does not track sessions for events delivered through Segment's
// cloud-mode destination unless each call carries a session_id (the epoch
// milliseconds at which the session started). We mirror Amplitude's default
// session semantics: a session ends after 30 minutes without any event.
// State is kept in localStorage so sessions survive full page reloads and
// are shared across tabs, like Amplitude's own SDK.
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_ID_STORAGE_KEY = "nira_analytics_session_id";
const SESSION_LAST_EVENT_AT_STORAGE_KEY = "nira_analytics_session_last_event";

// Even accessing window.localStorage throws a SecurityError when the browser
// blocks site data (e.g. Chrome with cookies disabled). Since session ids are
// computed synchronously inside UI event handlers, an unguarded access would
// break interactions for those users, so this is one of the rare places where
// try/catch is warranted. Falls back to in-memory storage: sessions then last
// for the lifetime of the tab, which is the best we can do without storage.
const getSessionStorageBackend = (): Storage | undefined => {
    // Probe with a write, not just a read: some browsers allow reads but
    // throw on writes (e.g. older Safari private mode).
    const probeKey = "nira_analytics_storage_probe";
    try {
        window.localStorage.setItem(probeKey, probeKey);
        window.localStorage.removeItem(probeKey);
        return window.localStorage;
    } catch {
        return undefined;
    }
};
const sessionStorageBackend = getSessionStorageBackend();
const inMemorySessionState = new Map<string, string>();

const readSessionValue = (key: string): string | null =>
    sessionStorageBackend !== undefined
        ? sessionStorageBackend.getItem(key)
        : inMemorySessionState.get(key) ?? null;

const writeSessionValue = (key: string, value: string): void => {
    if (sessionStorageBackend !== undefined) {
        sessionStorageBackend.setItem(key, value);
    } else {
        inMemorySessionState.set(key, value);
    }
};

const removeSessionValue = (key: string): void => {
    if (sessionStorageBackend !== undefined) {
        sessionStorageBackend.removeItem(key);
    } else {
        inMemorySessionState.delete(key);
    }
};

const getAmplitudeSessionId = (): number => {
    const now = Date.now();
    // A missing value reads as null, and Number(null) is 0; a malformed value
    // yields NaN. Both fail the `> 0` check, so we start a new session.
    const storedSessionId = Number(readSessionValue(SESSION_ID_STORAGE_KEY));
    const lastEventAt = Number(
        readSessionValue(SESSION_LAST_EVENT_AT_STORAGE_KEY)
    );
    const sessionId =
        storedSessionId > 0 && now - lastEventAt <= SESSION_TIMEOUT_MS
            ? storedSessionId
            : now;

    writeSessionValue(SESSION_ID_STORAGE_KEY, String(sessionId));
    writeSessionValue(SESSION_LAST_EVENT_AT_STORAGE_KEY, String(now));
    return sessionId;
};

// Passed as the Segment "options" argument on every call. The session_id is
// namespaced per destination; we include both the classic ("Amplitude") and
// Actions ("Actions Amplitude") destination keys so this works regardless of
// which destination type is enabled in the Segment workspace.
const getSegmentOptions = (): SegmentAnalytics.SegmentOpts => {
    const sessionId = getAmplitudeSessionId();
    return {
        integrations: {
            Amplitude: { session_id: sessionId },
            "Actions Amplitude": { session_id: sessionId }
        }
    };
};

export const page = (): void => {
    if (shouldSendMetrics()) {
        window.analytics.page({}, getSegmentOptions());
    }
};

export const track = (
    name: string,
    properties: Record<
        string,
        number | boolean | string | ReadonlyArray<number>
    >,
    // Invoked once analytics.js has handed the event to its destinations, so a
    // caller that is about to navigate the page away can wait for it. Invoked
    // synchronously when metrics are disabled, so call sites behave the same
    // way locally.
    callback?: () => void
): void => {
    if (shouldSendMetrics()) {
        window.analytics.track(name, properties, getSegmentOptions(), callback);
    } else {
        console.log(
            `📊 Segment Tracking name: ${name}, properties: `,
            properties
        );
        callback?.();
    }
};

/**
 * Reads the anonymous id analytics.js is currently using, or undefined before
 * the SDK itself has loaded — the loader snippet stubs the call queue, not this
 * accessor.
 */
const getAnonymousId = (): string | undefined => {
    if (typeof window.analytics.user !== "function") {
        return undefined;
    }
    return window.analytics.user().anonymousId() || undefined;
};

export const identify = (
    user: NiraAuth0UserType,
    callback: () => void
): void => {
    if (shouldSendMetrics()) {
        if (user.sub !== undefined && user.email !== undefined) {
            const segmentId = `${user.email}_${user.sub}`;
            window.analytics.identify(
                segmentId,
                {
                    email: user.email,
                    weekUserCreated: getMondayOfWeekCreated(user)
                },
                getSegmentOptions(),
                callback
            );
        }
    }
};

/**
 * Associates the current user with their account (the company-level "team"
 * entity, not the individual user) so events can be analyzed per account.
 * The group_type/group_value traits let the Amplitude destination map this
 * group call to an Amplitude group via its "Group Type Trait" and
 * "Group Value Trait" settings.
 */
export const group = (teamId: TeamId, teamName: string): void => {
    if (shouldSendMetrics()) {
        window.analytics.group(
            String(teamId),
            {
                name: teamName,
                group_type: "account",
                group_value: teamName
            },
            getSegmentOptions()
        );
    } else {
        console.log(
            `📊 Segment Group teamId: ${teamId}, teamName: ${teamName}`
        );
    }
};

/**
 * Clear the Segment identity on logout: analytics.js persists the user id,
 * traits and account group until something resets them.
 *
 * This is what keeps device_id stable across a user switch. analytics.js 2.0
 * throws away the anonymous id inside User.id() whenever the stored id changes
 * to a different non-null one, and the Amplitude destination maps that
 * anonymous id to device_id. Clearing the id here leaves the next login with
 * no previous id to differ from, so that branch never fires. It also stops the
 * departing person's traits, their email among them, sitting in the browser
 * afterwards, and clearing the session keys stops the next login continuing
 * their Amplitude session.
 *
 * The anonymous id is read before the reset and put back after, because
 * reset() mints a new one and a device id identifies the browser rather than
 * whoever is signed into it. Two user_ids on one device_id stay two Amplitude
 * users, so splitting the device gains nothing:
 * https://amplitude.com/docs/data/sources/instrument-track-unique-users#multiple-user-ids-on-the-same-device
 *
 * The account group goes too, but that is incidental: the event factory is
 * constructed with only the user, so groupId reaches Segment on group() calls
 * and never on page(), track() or identify().
 */
export const reset = (): void => {
    removeSessionValue(SESSION_ID_STORAGE_KEY);
    removeSessionValue(SESSION_LAST_EVENT_AT_STORAGE_KEY);
    if (shouldSendMetrics()) {
        const anonymousId = getAnonymousId();
        window.analytics.reset();
        if (anonymousId !== undefined) {
            window.analytics.setAnonymousId(anonymousId);
        }
    } else {
        console.log("📊 Segment Reset");
    }
};

const getMondayOfWeekCreated = (user: NiraAuth0UserType): string => {
    const mondayOfWeekCreated = new Date(user["https://nira"].created_at);
    const dayOfWeek = mondayOfWeekCreated.getDay() || 7; // Get current day number, converting Sun. to 7
    if (dayOfWeek !== 1) {
        // Only manipulate the date if it isn't Mon.
        mondayOfWeekCreated.setHours(-24 * (dayOfWeek - 1)); // Set the hours to day number minus 1 multiplied by negative 24
    }
    return mondayOfWeekCreated.toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "numeric",
        year: "numeric"
    });
};
